"use client";

import { useCallback, useEffect } from "react";
import { useSnapshot } from "valtio";
import { usePathname } from "next/navigation";
import log from "logbeacon";
import { HomeQuestionCard } from "../HomeQuestionCard";
import { ChatRefContent, SenderSubmitOptions } from "../Sender/type";
import { HomePageSender } from "./HomePageSender";
import { useHomePageCommonLogic } from "@/src/hooks/useHomePageCommonLogic";
import { buildRecommendScopes, HOME_RECOMMEND_SCOPE_MAP } from "@/src/const";
import { rootStore } from "@/src/store";
import { useIsPc, useRecommendQuestion } from "@/src/hooks";
import { useRecommendQuestionTracking } from "@/src/hooks/useRecommendQuestionTracking";
import {
  type StockType,
  getStockTypeFromPathname,
  DEFAULT_STOCK_TYPE,
} from "@/src/utils/stockType";

interface HomePageSenderWrapperProps {
  className?: string;
  /** 股票市场类型，优先使用传入的值 */
  stockType?: StockType;
  queryParams?: { [x: string]: number };
  chatRefContent?: ChatRefContent | null;
  setChatRefContent?: (content: ChatRefContent | null) => void;
}

export const HomePageSenderWrapper = ({
  stockType: propsStockType,
  queryParams,
  chatRefContent,
  setChatRefContent,
  className,
}: HomePageSenderWrapperProps = {}) => {
  const [isPc] = useIsPc();
  const { type } = useSnapshot(rootStore.home);
  const { sendLoading, onSend: originalOnSend } = useHomePageCommonLogic();
  const pathname = usePathname();
  // 优先使用传入的 stockType，fallback 到从 pathname 解析，最后使用默认值
  const stockType =
    propsStockType ?? getStockTypeFromPathname(pathname) ?? DEFAULT_STOCK_TYPE;

  // 使用推荐问题跟踪 hook
  const {
    value,
    setValue,
    handleChange,
    handleQuestionSelect,
    shouldUseRecommendInfo,
    getRecommendInfo,
    reset: resetTracking,
  } = useRecommendQuestionTracking();

  // 当 chatRefContent 变化时，自动设置输入框的值
  useEffect(() => {
    if (chatRefContent?.question) {
      setValue(chatRefContent.question);
    }
  }, [chatRefContent]);

  // 获取推荐问题数据（使用和 HomeQuestionCard 相同的数据源）
  const reqQueryParams =
    queryParams ?? buildRecommendScopes(type, 20, stockType);
  const { questions } = useRecommendQuestion(reqQueryParams, {
    enabled: true,
    staleTime: 1000 * 60 * 5,
    key:
      type === "crypto"
        ? HOME_RECOMMEND_SCOPE_MAP[type]
        : stockType === "HK"
          ? HOME_RECOMMEND_SCOPE_MAP.stock.HK
          : HOME_RECOMMEND_SCOPE_MAP.stock.US,
  });

  const handleSend = useCallback(
    (question: string | undefined, submitOptions?: SenderSubmitOptions) => {
      // 构建发送的问题文本，如果存在 chatRefContent，需要包含引用信息
      let finalQuestion = question || "";
      if (chatRefContent && chatRefContent.refId) {
        finalQuestion = `[${chatRefContent.title || ""}](referenceId=${chatRefContent.refId || chatRefContent.link || ""})\n${finalQuestion}`;
      }

      let requestParams = undefined;

      const isRecommend = shouldUseRecommendInfo(question);

      // 如果用户选中了推荐问题且没有修改输入，传递推荐问题信息
      if (isRecommend) {
        const selectedQuestion = getRecommendInfo();
        if (selectedQuestion) {
          log.info(
            `[onChartClick][trace]${isRecommend ? "[recommend]" : ""}[sender]`,
            requestParams,
            chatRefContent,
          );
          requestParams = {
            source_id: selectedQuestion.source_id || "",
            question: selectedQuestion.prompt || "",
          };
        }
      }
      originalOnSend(finalQuestion, requestParams, submitOptions);

      // 清空状态
      setChatRefContent?.(null);
      resetTracking();
    },
    [
      originalOnSend,
      chatRefContent,
      setChatRefContent,
      shouldUseRecommendInfo,
      getRecommendInfo,
      resetTracking,
    ],
  );

  const handleCloseChatToast = useCallback(() => {
    setChatRefContent?.(null);
  }, [setChatRefContent]);

  return (
    <HomePageSender
      onSend={handleSend}
      data={questions}
      loading={sendLoading}
      value={value}
      className={className}
      onChange={handleChange}
      onQuestionSelect={handleQuestionSelect}
      chatRefContent={chatRefContent}
      onCloseChatToast={handleCloseChatToast}
      senderQuestion={
        isPc ? (
          <HomeQuestionCard
            onSend={handleSend}
            className="pr-3 max-w-[calc(100%-94px)] xl:max-w-full w-full overflow-x-auto no-scrollbar"
          />
        ) : null
      }
    />
  );
};
